import { propState, type Command, type EntityState } from "../document/commands.ts";
import { sameJson } from "../document/same-json.ts";
import type { EntityInstance, PropInstance, WorldDocument } from "../document/types.ts";

/**
 * What a load changes in a document outside the history, as commands (ADR-086 decision 26): the catalogue's
 * tilesets appended to the list, following terrain layers named after what they paint, and whatever a host's
 * own upgrade changed of a prop or a marker. With a session these travel as the tab's first operations, so the
 * confirmed document stays the server's value for value and every tab ends on the same one.
 *
 * Only what those change is read: tilesets the wanted document appends, a layer's name and `autoName` flag, a
 * prop's own state, a prop the wanted layer adds to a prop layer both documents hold, and a marker removed, given
 * another type or changed in its place or fields. Anything else the two documents differ in (a marker or a layer the
 * wanted document adds, a prop it lacks, an order changed) is left as the server holds it. A host's upgrade that adds
 * a prop gives it an id that is a function of the document, so two tabs sending the same load add one prop.
 */

/**
 * A marker's state, the part `updateEntity` changes.
 *
 * @param entity The marker.
 * @returns Its place and fields.
 */
function entityStateOf(entity: EntityInstance): EntityState {
  return { x: entity.x, y: entity.y, fields: entity.fields };
}

/**
 * The commands that take a marker layer's markers to the wanted ones: a marker the wanted layer lacks is
 * removed; one of another type is removed and added again in its place, since no command changes a type; one
 * whose place or fields differ is updated. The removals run from the end of the list, so each index holds
 * when its command applies.
 *
 * @param mapId The map.
 * @param layerId The layer.
 * @param before The markers as loaded.
 * @param after The markers wanted.
 * @returns The commands, in an order that applies to `before`.
 */
function entityCommands(mapId: string, layerId: string, before: readonly EntityInstance[], after: readonly EntityInstance[]): Command[] {
  const wanted = new Map(after.map((entity) => [entity.id, entity]));
  const commands: Command[] = [];
  const updates: Command[] = [];
  for (let index = before.length - 1; index >= 0; index -= 1) {
    const old = before[index] as EntityInstance;
    const now = wanted.get(old.id);
    if (now === undefined || now.type !== old.type) {
      commands.push({ kind: "removeEntity", mapId, layerId, index, entity: old });
      if (now !== undefined) {
        commands.push({ kind: "addEntity", mapId, layerId, index, entity: now });
      }
    } else if (now !== old && !sameJson(entityStateOf(old), entityStateOf(now))) {
      updates.unshift({ kind: "updateEntity", mapId, layerId, entityId: old.id, before: entityStateOf(old), after: entityStateOf(now) });
    }
  }
  return [...commands, ...updates];
}

/**
 * The commands that add to a prop layer the props the wanted layer holds and the loaded one does not. Each goes in
 * after the nearest prop before it in the wanted layer that the layer then holds, or first when there is none, so
 * its index holds when its command applies, whatever else the loaded layer keeps.
 *
 * @param mapId The map.
 * @param layerId The layer.
 * @param before The props as loaded.
 * @param after The props wanted.
 * @returns The commands, in an order that applies to `before`.
 */
function addedProps(mapId: string, layerId: string, before: readonly PropInstance[], after: readonly PropInstance[]): Command[] {
  const running = before.map((prop) => prop.id);
  const commands: Command[] = [];
  let index = 0;
  for (const prop of after) {
    const at = running.indexOf(prop.id);
    if (at >= 0) {
      index = at + 1;
      continue;
    }
    running.splice(index, 0, prop.id);
    commands.push({ kind: "addProp", mapId, layerId, index, prop });
    index += 1;
  }
  return commands;
}

/**
 * The commands that take the document a tab loaded to the one its editor wants to open on.
 *
 * @param loaded The document as the server holds it.
 * @param wanted The same document after the load's own changes.
 * @returns The commands in an order that applies to `loaded`: the tilesets first, then each map's renames,
 *   prop updates and marker changes in document order. None when the two agree in what a load changes.
 */
export function loadCommands(loaded: WorldDocument, wanted: WorldDocument): Command[] {
  const commands: Command[] = [];
  if (wanted !== loaded) {
    const listed = new Set(loaded.tilesets.map((tileset) => tileset.id));
    const missing = wanted.tilesets.filter((tileset) => !listed.has(tileset.id));
    if (missing.length > 0) {
      commands.push({ kind: "changeTilesets", remove: [], insert: missing.map((tileset, i) => ({ index: loaded.tilesets.length + i, id: tileset.id })) });
    }
  }
  for (const map of wanted.maps) {
    const held = loaded.maps.find((candidate) => candidate.id === map.id);
    if (held === undefined || held === map) {
      continue;
    }
    for (const layer of map.layers) {
      const before = held.layers.find((candidate) => candidate.id === layer.id);
      if (before === undefined || before === layer || before.kind !== layer.kind) {
        continue;
      }
      const was = before.kind === "terrain" ? before.autoName : undefined;
      const now = layer.kind === "terrain" ? layer.autoName : undefined;
      if (before.name !== layer.name || was !== now) {
        const flag = was === now ? undefined : { ...(was === undefined ? {} : { before: was }), ...(now === undefined ? {} : { after: now }) };
        commands.push({ kind: "renameLayer", mapId: map.id, layerId: layer.id, before: before.name, after: layer.name, ...(flag === undefined ? {} : { autoName: flag }) });
      }
      if (before.kind === "prop" && layer.kind === "prop" && before.props !== layer.props) {
        const props = new Map(before.props.map((prop) => [prop.id, prop]));
        for (const prop of layer.props) {
          const old = props.get(prop.id);
          if (old !== undefined && old !== prop && !sameJson(propState(old), propState(prop))) {
            commands.push({ kind: "updateProp", mapId: map.id, layerId: layer.id, propId: prop.id, before: propState(old), after: propState(prop) });
          }
        }
        commands.push(...addedProps(map.id, layer.id, before.props, layer.props));
      }
      if (before.kind === "entity" && layer.kind === "entity" && before.entities !== layer.entities) {
        commands.push(...entityCommands(map.id, layer.id, before.entities, layer.entities));
      }
    }
  }
  return commands;
}
